import { useEffect, useState } from "react";
import { api } from "../api/apiClient";
import type { Shift } from "../api/types";
import { formatTime24 } from "../utils/time24";

interface ClockInOutCardProps {
  shift: Shift;
  onChanged: (next: Shift) => void;
}

function formatDuration(ms: number): string {
  const totalMinutes = Math.max(0, Math.floor(ms / 60000));
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

export function ClockInOutCard({ shift, onChanged }: ClockInOutCardProps) {
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (shift.status !== "IN_PROGRESS") return;
    const handle = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(handle);
  }, [shift.status]);

  const plannedStart = new Date(shift.plannedStart).getTime();
  const plannedEnd = new Date(shift.plannedEnd).getTime();

  // G-50 deleted the early-clock-in gate on the server (D-34 removed the copy
  // that lingered here): a scheduled shift can be started any time before its
  // planned end; starting early pays from the actual start.
  const canClockIn = shift.status === "SCHEDULED" && now <= plannedEnd;

  const elapsedMs = shift.actualStart
    ? now - new Date(shift.actualStart).getTime()
    : 0;

  const handleClockIn = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.clockIn(shift.id);
      onChanged(updated);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not clock in");
    } finally {
      setBusy(false);
    }
  };

  const handleClockOut = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.clockOut(shift.id);
      onChanged(updated);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not clock out");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="clock-card">
      <div>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
          Today at {shift.location.name}
        </h3>
        <p className="muted" style={{ margin: "4px 0 0", fontSize: 14 }}>
          Planned {formatTime24(shift.plannedStart)}–{formatTime24(shift.plannedEnd)}
        </p>
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      {shift.status === "SCHEDULED" ? (
        <>
          <p style={{ margin: 0 }}>
            {canClockIn
              ? "Tap below to start your shift."
              : "Planned end has already passed — ask your manager to log this manually."}
          </p>
          <div className="button-row" style={{ marginTop: 0 }}>
            <button
              type="button"
              className="button"
              onClick={() => void handleClockIn()}
              disabled={!canClockIn || busy}
            >
              {busy ? "…" : "Clock in"}
            </button>
          </div>
        </>
      ) : null}

      {shift.status === "IN_PROGRESS" && shift.actualStart ? (
        <>
          <div className="clock-card-times">
            <div>
              <strong>Started</strong>
              {formatTime24(shift.actualStart)}
            </div>
            <div>
              <strong>Elapsed</strong>
              {formatDuration(elapsedMs)}
            </div>
          </div>
          <div className="button-row" style={{ marginTop: 0 }}>
            <button
              type="button"
              className="button danger"
              onClick={() => void handleClockOut()}
              disabled={busy}
            >
              {busy ? "…" : "Clock out"}
            </button>
          </div>
        </>
      ) : null}

      {shift.status === "COMPLETED" && shift.actualStart && shift.actualEnd ? (
        <div className="clock-card-times">
          <div>
            <strong>Actual</strong>
            {formatTime24(shift.actualStart)}–{formatTime24(shift.actualEnd)}
          </div>
          <div>
            <strong>Worked</strong>
            {formatDuration(
              new Date(shift.actualEnd).getTime() -
                new Date(shift.actualStart).getTime(),
            )}
          </div>
          <div>
            <strong>Vs plan</strong>
            {formatDuration(
              new Date(shift.actualEnd).getTime() -
                new Date(shift.actualStart).getTime() -
                (plannedEnd - plannedStart),
            )}
          </div>
        </div>
      ) : null}

      {shift.status === "MISSED" ? (
        <p className="alert error" style={{ margin: 0 }}>
          Marked as missed — talk to your manager to correct the actual times.
        </p>
      ) : null}
    </div>
  );
}
